import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { Subscription, timer } from "rxjs";
import { OrderService } from "@app/shared/services/order/order.service";
import { NotificationService } from "@app/core/services/notification/notification.service";
import { BulkJob, BulkJobItem } from "../bulk-job.model";

/**
 * WI 1711: status view for a "Mark Shipped in Impress Only" bulk job. Opened via MatDialog with
 * either { jobId } (status mode, polls every 3s while Queued/Running) or { jobs } (Recent uploads
 * list). Standalone so it needs no module declaration.
 */
@Component({
  selector: "app-bulk-job-status",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatFormFieldModule,
    MatSelectModule,
  ],
  templateUrl: "./bulk-job-status.component.html",
  styleUrls: ["./bulk-job-status.component.scss"],
})
export class BulkJobStatusComponent implements OnInit, OnDestroy {
  jobId: number | null = null;
  recentJobs: BulkJob[] = [];
  showRecent = false;

  job: BulkJob | null = null;
  items: BulkJobItem[] = [];
  filteredItems: BulkJobItem[] = [];
  outcomeFilter = "All";
  outcomes = [
    "All",
    "Added",
    "AlreadyShipped",
    "ImpressError",
    "NoTracking",
    "NoOrderDeskLink",
    "NotFound",
  ];

  loading = false;
  downloading = false;
  private pollSub?: Subscription;

  constructor(
    public dialogRef: MatDialogRef<BulkJobStatusComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { jobId?: number; jobs?: BulkJob[] },
    private orderService: OrderService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    if (this.data?.jobs && this.data.jobs.length) {
      this.recentJobs = this.data.jobs;
      this.showRecent = true;
    }
    if (this.data?.jobId) {
      this.openJob(this.data.jobId);
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  openJob(id: number): void {
    this.jobId = id;
    this.showRecent = false;
    this.job = null;
    this.items = [];
    this.filteredItems = [];
    this.fetch(true);
  }

  backToRecent(): void {
    this.stopPolling();
    this.job = null;
    this.items = [];
    this.filteredItems = [];
    this.showRecent = true;
  }

  private fetch(initial = false): void {
    if (!this.jobId) {
      return;
    }
    if (!this.job) {
      this.loading = true;
    }
    this.orderService.getBulkJob(this.jobId).subscribe({
      next: (res: any) => {
        this.loading = false;
        if (res?.StatusCode === 200 && res?.Data?.ResponseData) {
          this.job = res.Data.ResponseData as BulkJob;
          this.items = this.job.Items || [];
          this.applyFilter();
          if (this.isActiveStatus(this.job.Status)) {
            if (initial) {
              this.startPolling();
            }
          } else {
            this.stopPolling();
          }
        }
      },
      error: () => {
        this.loading = false;
        this.stopPolling();
      },
    });
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollSub = timer(3000, 3000).subscribe(() => this.fetch(false));
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }

  private isActiveStatus(status: string): boolean {
    return status === "Queued" || status === "Running";
  }

  applyFilter(): void {
    this.filteredItems =
      this.outcomeFilter === "All"
        ? this.items
        : this.items.filter((i) => i.Outcome === this.outcomeFilter);
  }

  get progress(): number {
    if (!this.job || !this.job.Total) {
      return 0;
    }
    return Math.round((this.job.Processed / this.job.Total) * 100);
  }

  nextStep(outcome: string): string {
    switch (outcome) {
      case "Added":
        return "Marked shipped in Impress.";
      case "AlreadyShipped":
        return "Shipped in Impress, needs invoicing.";
      case "NoTracking":
        return "No tracking found.";
      case "NoOrderDeskLink":
        return "No OrderDesk link.";
      case "NotFound":
        return "Order not found in MerchLoop.";
      case "ImpressError":
        return "Impress rejected the shipment.";
      default:
        return "";
    }
  }

  download(): void {
    if (!this.jobId) {
      return;
    }
    this.downloading = true;
    const id = this.jobId;
    this.orderService.exportBulkJob(id).subscribe({
      next: (blob: Blob) => {
        this.downloading = false;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "MarkShipped_Results_" + id + ".xlsx";
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.downloading = false;
        this.notificationService.error("Could not download results.", "Error");
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
